import { QuestionnaireRollout, RevisionRollout, RolloutStatus } from "./api";

export const groupLabel = (group: string) => (group === "prod" ? "Prod" : group === "stage" ? "Stage" : group);

// Attribution is only carried by the entry the group currently points at, so it reads as noise
// ("deployer unknown") on statuses that were never going to have it.
export const isDeployTarget = (status: RolloutStatus) =>
  status === "active" || status === "activating" || status === "stalled";

export const deployedGroups = (rollout: RevisionRollout[]) =>
  rollout.filter(entry => isDeployTarget(entry.status)).map(entry => groupLabel(entry.group));

export const isDeployedTo = (rollout: RevisionRollout[], group: string) =>
  rollout.some(entry => entry.group === group && isDeployTarget(entry.status));

export const timeAgo = (iso: string) => {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleDateString();
};

type DeployAttribution = {
  deployed_by_email?: string | null;
  deployed_at?: string | null;
};

export const deployAttribution = ({ deployed_by_email, deployed_at }: DeployAttribution) => {
  if (deployed_by_email && deployed_at) return `by ${deployed_by_email} ${timeAgo(deployed_at)}`;
  if (deployed_by_email) return `by ${deployed_by_email}`;
  if (deployed_at) return `${timeAgo(deployed_at)}`;
  return "deployer unknown";
};

const revisionDate = (iso: string) => new Date(iso).toLocaleDateString();

const revisionTime = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const revisionAt = (entry: QuestionnaireRollout, fallback: string) => entry.target_published_at ?? fallback;

const byRevision = (entries: QuestionnaireRollout[]) =>
  entries.reduce<QuestionnaireRollout[][]>((clusters, entry) => {
    const runs = (cluster: QuestionnaireRollout[]) =>
      cluster[0].target_published_questionnaire_id === entry.target_published_questionnaire_id;
    return clusters.some(runs)
      ? clusters.map(cluster => (runs(cluster) ? [...cluster, entry] : cluster))
      : [...clusters, [entry]];
  }, []);

// Groups are rolled out one at a time, so a name normally runs one revision on stage and an older
// one on prod. The collapsed row is the only place both are visible at once, so it dates the
// revision each group runs rather than the last publish — a row of badges alone reads as "deployed
// everywhere" and the drift only surfaces after expanding the revision list. Groups sharing a
// revision share its date; when the revisions were cut on the same day the date is stated once and
// the groups are told apart by the time.
export const deployedRevisions = (rollout: QuestionnaireRollout[], publishedAt: string) => {
  const deployed = rollout.filter(entry => entry.status !== "inactive");
  if (deployed.length === 0 || deployed.some(entry => !entry.target_published_at))
    return { date: revisionDate(publishedAt), revisions: [] };

  const clusters = byRevision(deployed);
  const dates = new Set(deployed.map(entry => revisionDate(revisionAt(entry, publishedAt))));
  const sameDay = clusters.length > 1 && dates.size === 1;
  const label = sameDay ? revisionTime : revisionDate;

  return {
    date: sameDay ? revisionDate(revisionAt(deployed[0], publishedAt)) : undefined,
    revisions: clusters.map(cluster => ({ groups: cluster, when: label(revisionAt(cluster[0], publishedAt)) })),
  };
};
